/**
 * One person: who they are, every submission they sent and why each is
 * theirs, suggestions that someone else may be them, and their timeline.
 *
 * A person's id can change under the page: an email-grouped id (`e…`)
 * becomes a stored one (`p…`) once stored people are on, and a merge keeps
 * one of two. The server answers with the id to show, and the address is
 * replaced with it, so a link copied from here keeps working.
 *
 * Merging and unlinking need stored people and the link_people permission;
 * the server says both in `can_link`. Every change says what it did and
 * offers Undo, as does each merge or unlink on the timeline.
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, Modal, Stack, Table, Tabs, Text, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconGitMerge, IconMail, IconPhone, IconUnlink } from '@tabler/icons-react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  useDecideSuggestions, usePerson, useUndoLink, useUnlinkSubmission,
  type IdentitySuggestion, type PersonDetail, type PersonSubmission,
} from '../../api/people';
import { FONTS, TOKENS, formatGBP, formatNumber } from '../../theme';
import { PageHeader } from '../../ui';
import { relativeTime } from '../../ui/format';
import { useVocab, type Vocab } from '../../vocab';
import { formatDay, useCrmPermissions } from '../crm/crmShared';
import { TimelinePanel } from '../crm/Timeline';
import { MergePersonModal } from './MergePersonModal';
import { PersonOwner } from '../assignment/OwnerControls';
import { PropertiesPanel } from '../properties/PropertyFields';
import { useUpdatePersonProperties } from '../../api/properties';
import { useScoringSettings, type PersonScore } from '../../api/scoring';
import { ScoreBadge, bandFor, signed } from '../scoring/scoringShared';
import {
  Figure, LinkedByChip, notifyWithUndo, personName, PersonBriefView, ruleLabel, StreamChips,
} from './peopleShared';

const LeadDetailModal = lazy(() =>
  import('../LeadDetailModal').then((m) => ({ default: m.LeadDetailModal })),
);

export function PersonPage() {
  const { id = '' } = useParams();
  const v = useVocab();
  const perms = useCrmPermissions();
  const navigate = useNavigate();
  const { data: person, isPending, error } = usePerson(id, perms.seesPeople);
  const [tab, setTab] = useState<string | null>('submissions');
  const [merging, setMerging] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);

  // The server may answer under another id; show that one.
  useEffect(() => {
    if (person && person.id !== id) navigate(`/people/${encodeURIComponent(person.id)}`, { replace: true });
  }, [person, id, navigate]);

  if (!perms.seesPeople) return <Navigate to="/" replace />;

  const canLink = !!person?.can_link && perms.linkPeople;

  return (
    <Stack gap="md">
      <Anchor component={Link} to="/people" size="sm" c="dimmed">
        <Group gap={4}><IconArrowLeft size={14} />{v.Many('person')}</Group>
      </Anchor>
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {person && (
        <>
          <PageHeader
            title={personName(person)}
            status={(
              <Group gap={6} wrap="nowrap">
                <Badge variant="light" color="gray" radius="sm" tt="none">
                  {v.count(person.submission_count, 'lead')}
                </Badge>
                {person.score && <ScoreBadge score={person.score.total} band={person.score.band} withLabel size="md" />}
              </Group>
            )}
            actions={canLink && (
              <Button variant="default" leftSection={<IconGitMerge size={16} />} onClick={() => setMerging(true)}>
                {`Merge another ${v.one('person')}`}
              </Button>
            )}
          />

          <Identity person={person} />

          <PersonOwner
            personId={person.id}
            ownerId={person.owner_user_id}
            ownerName={person.owner_name}
            canSetOwner={person.can_set_owner}
            canAssign={person.can_assign && perms.assignRecords}
          />

          <Group gap="xl" wrap="wrap">
            <Figure label={v.Many('lead')} value={formatNumber(person.submission_count)} />
            <Figure label="Value" value={formatGBP(person.total_value)} />
            <Figure label={v.p.Sold} value={formatNumber(person.sold)} />
            <Figure label="First seen" value={formatDay(person.first_seen)} />
            <Figure label="Last seen" value={formatDay(person.last_seen)} />
          </Group>
          {person.score && <ScoreParts score={person.score} v={v} />}
          {person.streams.length > 0 && (
            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">{`${v.Many('campaign')}:`}</Text>
              <StreamChips streams={person.streams} max={8} />
            </Group>
          )}

          {person.source === 'email' && (
            <Text size="sm" c="dimmed">
              Grouped by email until stored people are switched on. Merging and unlinking need them.
            </Text>
          )}

          {perms.recordProperties && person.can_set_owner && <PersonProperties person={person} />}

          {person.suggestions.length > 0 && (
            <Suggestions person={person} canLink={canLink} v={v} />
          )}

          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="submissions">{`${v.Many('lead')} (${formatNumber(person.submission_count)})`}</Tabs.Tab>
              <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="submissions" pt="md">
              <Submissions person={person} canLink={canLink} onOpen={setLeadId} />
            </Tabs.Panel>
            <Tabs.Panel value="timeline" pt="md">
              <Card p="md" maw={760}>
                <TimelinePanel objectType="person" objectId={person.id} />
              </Card>
            </Tabs.Panel>
          </Tabs>

          <MergePersonModal opened={merging} person={person} onClose={() => setMerging(false)} />
          {leadId && (
            <Suspense fallback={null}>
              <LeadDetailModal id={leadId} onClose={() => setLeadId(null)} />
            </Suspense>
          )}
        </>
      )}
    </Stack>
  );
}

/** A stored person's properties, edited with edit rights on the person. */
function PersonProperties({ person }: { person: PersonDetail }) {
  const update = useUpdatePersonProperties();
  return (
    <div style={{ maxWidth: 760 }}>
      <PropertiesPanel
        object="person"
        values={(person.properties ?? {}) as Record<string, unknown>}
        canEdit={person.can_edit_properties}
        saving={update.isPending}
        onSave={(patch) => update.mutateAsync({ id: person.id, properties: patch })}
      />
    </div>
  );
}

function Identity({ person }: { person: PersonDetail }) {
  const emails = person.emails.length ? person.emails : [person.email].filter(Boolean);
  const phones = person.phones.length ? person.phones : [person.phone].filter(Boolean);
  if (!emails.length && !phones.length) return null;
  return (
    <Group gap="lg" wrap="wrap" data-testid="person-identity">
      {emails.map((e) => (
        <Group key={e} gap={6} wrap="nowrap">
          <IconMail size={14} color={TOKENS.textMuted} />
          <Text size="sm" style={{ overflowWrap: 'anywhere' }}>{e}</Text>
        </Group>
      ))}
      {phones.map((p) => (
        <Group key={p} gap={6} wrap="nowrap">
          <IconPhone size={14} color={TOKENS.textMuted} />
          <Text size="sm" style={{ fontFamily: FONTS.mono }}>{p}</Text>
        </Group>
      ))}
    </Group>
  );
}

/** "These may be the same person", from this person's side. */
function Suggestions({ person, canLink, v }: { person: PersonDetail; canLink: boolean; v: Vocab }) {
  const decide = useDecideSuggestions();
  const undo = useUndoLink();
  const navigate = useNavigate();

  async function act(s: IdentitySuggestion, action: 'accept' | 'dismiss') {
    try {
      const r = await decide.mutateAsync({ ids: [s.id], action });
      if (action === 'dismiss') {
        notifications.show({ color: 'teal', title: 'Kept apart', message: 'Not suggested again.' });
        return;
      }
      if (r.accepted === 0) {
        notifications.show({ color: 'gray', title: 'Nothing to link', message: 'Already decided, or already one.' });
        return;
      }
      notifyWithUndo({
        title: 'Linked',
        message: `${personName(other(s, person.id))} is now part of this ${v.one('person')}.`,
        undo: async () => {
          try {
            for (const id of r.link_ids) await undo.mutateAsync(id);
            notifications.show({ color: 'teal', title: 'Undone', message: 'They are apart again.' });
          } catch (e) {
            notifications.show({ color: 'red', title: 'Not undone', message: (e as Error).message });
          }
        },
      });
      // The older of the two is kept, which may not be this one.
      const kept = s.candidate.id;
      if (kept !== person.id) navigate(`/people/${encodeURIComponent(kept)}`, { replace: true });
    } catch (e) {
      notifications.show({ color: 'red', title: action === 'accept' ? 'Not linked' : 'Not changed', message: (e as Error).message });
    }
  }

  return (
    <Card p="md" withBorder data-testid="person-suggestions" style={{ borderColor: 'var(--mantine-color-chartAmber-4)' }}>
      <Stack gap="sm">
        <Text size="sm" fw={600}>{`May be the same ${v.one('person')}`}</Text>
        {person.suggestions.map((s) => {
          const o = other(s, person.id);
          return (
            <Group key={s.id} justify="space-between" align="center" wrap="wrap" gap="sm">
              <Group gap="md" wrap="wrap" style={{ minWidth: 0, flex: '1 1 260px' }}>
                <Anchor component={Link} to={`/people/${encodeURIComponent(o.id)}`} c="inherit" style={{ minWidth: 0 }}>
                  <PersonBriefView person={o} v={v} />
                </Anchor>
                <Badge variant="light" color="chartAmber" radius="sm" tt="none" style={{ flex: 'none' }}>
                  {ruleLabel(s.rule)}
                </Badge>
              </Group>
              {canLink && (
                <Group gap="xs">
                  <Button size="xs" onClick={() => act(s, 'accept')} loading={decide.isPending}>Link</Button>
                  <Button size="xs" variant="default" onClick={() => act(s, 'dismiss')} disabled={decide.isPending}>
                    Not them
                  </Button>
                </Group>
              )}
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}

/** The other side of a suggestion. */
function other(s: IdentitySuggestion, me: string) {
  return s.person.id === me ? s.candidate : s.person;
}

function Submissions({
  person, canLink, onOpen,
}: { person: PersonDetail; canLink: boolean; onOpen: (id: string) => void }) {
  const v = useVocab();
  const unlink = useUnlinkSubmission();
  const undo = useUndoLink();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState<PersonSubmission | null>(null);
  const [error, setError] = useState('');
  const canUnlink = canLink && person.submissions.length > 1;
  const scored = person.submissions.some((s) => s.score != null);
  const bands = useScoringSettings(scored).data?.bands;

  async function doUnlink() {
    if (!confirming) return;
    setError('');
    try {
      const r = await unlink.mutateAsync(confirming.id);
      setConfirming(null);
      notifyWithUndo({
        title: 'Unlinked',
        message: `That ${v.one('lead')} is now ${v.a('person')} of its own.`,
        undo: async () => {
          try {
            const back = await undo.mutateAsync(r.link.id);
            notifications.show({ color: 'teal', title: 'Undone', message: `The ${v.one('lead')} is back with ${personName(person)}.` });
            if (back.person_id !== person.id) navigate(`/people/${encodeURIComponent(back.person_id)}`);
          } catch (e) {
            notifications.show({ color: 'red', title: 'Not undone', message: (e as Error).message });
          }
        },
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Card p={0}>
      <Table.ScrollContainer minWidth={820}>
        <Table verticalSpacing={8} highlightOnHover data-testid="person-submissions">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Arrived</Table.Th>
              <Table.Th>{v.One('campaign')}</Table.Th>
              <Table.Th>As sent</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Value</Table.Th>
              {scored && <Table.Th>Score</Table.Th>}
              <Table.Th>Linked by</Table.Th>
              {canUnlink && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {person.submissions.map((s) => (
              <Table.Tr key={s.id} onClick={() => onOpen(s.id)} style={{ cursor: 'pointer' }}>
                <Table.Td>
                  <Tooltip label={formatDay(s.created_at)} withArrow openDelay={300}>
                    <Text size="sm" span>{relativeTime(s.created_at)}</Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td><Text size="sm" fw={500} lineClamp={1}>{s.campaign_name}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>{s.fullname || '(no name)'}</Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>{[s.email, s.phone1].filter(Boolean).join(' · ')}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={s.sold ? 'chartGreen' : s.delivery_status === 'rejected' ? 'chartRed' : 'gray'}
                  >
                    {s.sold ? v.p.Sold : s.delivery_status || s.status}
                  </Badge>
                  {s.buyer_name && <Text size="xs" c="dimmed" lineClamp={1}>{s.buyer_name}</Text>}
                </Table.Td>
                <Table.Td ta="right">
                  <span style={{ fontFamily: FONTS.mono, fontSize: 13 }}>{formatGBP(s.revenue)}</span>
                </Table.Td>
                {scored && <Table.Td><ScoreBadge score={s.score} band={bandFor(bands, s.score)} size="xs" /></Table.Td>}
                <Table.Td><LinkedByChip linkedBy={s.linked_by} /></Table.Td>
                {canUnlink && (
                  <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      leftSection={<IconUnlink size={12} />}
                      onClick={() => { setError(''); setConfirming(s); }}
                    >
                      Unlink
                    </Button>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {person.submission_count > person.submissions.length && (
        <Text size="xs" c="dimmed" p="sm">
          {`Showing the newest ${formatNumber(person.submissions.length)} of ${formatNumber(person.submission_count)}.`}
        </Text>
      )}

      <Modal
        opened={!!confirming}
        onClose={() => setConfirming(null)}
        title={`Unlink this ${v.one('lead')}?`}
        size="md"
      >
        {confirming && (
          <Stack>
            <Text size="sm">
              {`${confirming.fullname || confirming.email || `This ${v.one('lead')}`}, ${confirming.campaign_name}, ${formatDay(confirming.created_at)}, comes out of ${personName(person)} and becomes ${v.a('person')} of its own. You can undo this.`}
            </Text>
            {error && <Alert color="red" variant="light" title="Not unlinked">{error}</Alert>}
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setConfirming(null)}>Cancel</Button>
              <Button color="red" onClick={doUnlink} loading={unlink.isPending}>Unlink</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Card>
  );
}

/**
 * How a person's score was reached: where it starts (their best or newest
 * lead), what the person rules added, and what time without a new lead took
 * away.
 */
function ScoreParts({ score, v }: { score: PersonScore; v: Vocab }) {
  const parts = [
    `${score.base} from their ${score.base_from === 'latest' ? 'newest' : 'best'} ${v.one('lead')}`,
    ...score.matched.map((m) => `${signed(m.points)} ${m.name}`),
    ...(score.decay > 0 ? [`-${score.decay} for time without a new ${v.one('lead')}`] : []),
  ];
  return (
    <Group gap="xs" wrap="nowrap" align="center" data-testid="person-score">
      <Text size="xs" c="dimmed">Score:</Text>
      <Text size="sm">{parts.join(', ')}.</Text>
    </Group>
  );
}
